import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { PiasLayoutComponent } from '../../../../shared/components/pias-layout/pias-layout.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectOptions } from '../../../../core/interfaces/select-options.interface';
import { Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { SessionStorageService } from '../../../../core/services/session-storage/session-storage.service';
import { RecrutmentMangerComponentsService } from '../recruitment-manger-services/recrutment-manger-components.service';
import { SnackbarService } from '../../../../shared/services/snackbar/snackbar.service';
import { OnboardingApplication } from '../recruitment-manger-components-models/onboarding-application/onboarding-application.model';
import { HttpStatusCode } from 'axios';
import { ConfirmPopupComponent } from '../../new-rep-application/confirm-popup/confirm-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { ViewApplicationdetails } from '../recruitment-manger-interfaces/view-applicationdetails.interface';


@Component({
  selector: 'app-edit-onboarding-application',
  imports: [
    CommonModule,
    PiasLayoutComponent,
    RouterModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    ReactiveFormsModule,
    MatDividerModule
  ],
  templateUrl: './edit-onboarding-application.component.html',
  styleUrl: './edit-onboarding-application.component.scss'
})
export class EditOnboardingApplicationComponent implements OnInit {

  private formBuilder = inject(FormBuilder);
  private router = inject(Router);
  private sessionStorageService = inject(SessionStorageService);
  private recruitmentManagerService = inject(RecrutmentMangerComponentsService);
  private snackBarService = inject(SnackbarService);
  private matDialog = inject(MatDialog);
  setSprinner = signal(false);
  disabled = signal(true);
  viewId!: number;
  data = history.state;
  protected currentUserRole: string = "";

  editOnboardApplicationFg!: FormGroup;
  protected Applicationdetails!: ViewApplicationdetails;
  protected designations!: SelectOptions[];
  readonly DESIGNATION = {
    FAR: 'Financial Advisor Representative',
    FAM: 'Financial Advisor Manager',
    INDIVIDUAL: 'Introducer - Individual',
    CORPORATE: 'Introducer - Corporate',
  };
  get designation(): string {
    return this.editOnboardApplicationFg.get('designation')?.value;
  }

  get showFields(): boolean {
    return !!this.designation;
  }

  get isAgent(): boolean {
    return (
      this.designation === this.DESIGNATION.FAR ||
      this.designation === this.DESIGNATION.FAM
    );
  }

  get isIndividual(): boolean {
    return this.designation === this.DESIGNATION.INDIVIDUAL;
  }

  get isCorporate(): boolean {
    return this.designation === this.DESIGNATION.CORPORATE;
  }

  get showName(): boolean {
    return this.showFields && !this.isCorporate;
  }

  get showPreferredName(): boolean {
    return !this.isCorporate;
  }

  get showBranch(): boolean {
    return !this.isCorporate;
  }

  get showDirector(): boolean {
    return !this.isCorporate;
  }

  get showManager(): boolean {
    return !this.isCorporate;
  }

  get showReferral(): boolean {
    return this.showFields && this.isAgent;
  }

  get showCompany(): boolean {
    return this.showFields && this.isCorporate;
  }

  get showEmail(): boolean {
    return this.showFields;
  }

  get showRemarks(): boolean {
    return this.showFields;
  }

  get emailLabel(): string {
    return this.isIndividual || this.isCorporate ? 'Email Address' : 'Personal EmailAddress';
  }

  ngOnInit(): void {
    this.editOnboardApplicationFg = this.formBuilder.group({
      name: ['', [Validators.required]],
      preferredName: [''],
      branch: [''],
      financialServicesDirector: [''],
      financialServicesManager: [''],
      designation: ['', [Validators.required]],
      nameofReferral: [''],
      companyName: [''],
      personalEmailAddress: ['', [Validators.required, Validators.email]],
      remarksbyRecruitmentManager: [''],

    });

    this.currentUserRole = this.sessionStorageService.getCurrentUserRole();

    if (this.currentUserRole == 'Advisor') {
      this.designations = [
        {
          name: "Introducer - Individual",
          value: "Introducer - Individual"
        },
        {
          name: "Introducer - Corporate",
          value: "Introducer - Corporate"
        }
      ];

    } else {
      this.designations = [
        {
          name: "Financial Advisor Representative",
          value: "Financial Advisor Representative"
        },
        {
          name: "Financial Advisor Manager",
          value: "Financial Advisor Manager"
        },
        {
          name: "Introducer - Individual",
          value: "Introducer - Individual"
        },
        {
          name: "Introducer - Corporate",
          value: "Introducer - Corporate"
        }
      ];

    }

    this.updateValidatorsForDesignation();
    this.editOnboardApplicationFg.get('designation')?.valueChanges.subscribe(() => {
      this.resetConditionalFields();
      this.updateValidatorsForDesignation();
    });

    this.editOnboardApplicationFg.valueChanges.subscribe(() => {
      const identifierValue = this.isCorporate
        ? this.editOnboardApplicationFg.get('companyName')?.value
        : this.editOnboardApplicationFg.get('name')?.value;
      this.disabled.set(!identifierValue);
    });
    this.viewId = this.data.id;
    this.viewApplicationDetails();

  }

  private updateValidatorsForDesignation(): void {
    const nameControl = this.editOnboardApplicationFg.get('name');
    const companyNameControl = this.editOnboardApplicationFg.get('companyName');

    if (this.isCorporate) {
      nameControl?.clearValidators();
      companyNameControl?.setValidators([Validators.required]);
    } else {
      nameControl?.setValidators([Validators.required]);
      companyNameControl?.clearValidators();
    }

    nameControl?.updateValueAndValidity({ emitEvent: false });
    companyNameControl?.updateValueAndValidity({ emitEvent: false });
  }

  private resetConditionalFields(): void {
    const fieldsToReset = [
      'name',
      'preferredName',
      'companyName',
      'nameofReferral',
      'personalEmailAddress',
      'remarksbyRecruitmentManager'
    ];

    fieldsToReset.forEach((controlName) => {
      const control = this.editOnboardApplicationFg.get(controlName);
      control?.setValue('', { emitEvent: false });
      control?.markAsPristine();
      control?.markAsUntouched();
    });
  }

  async viewApplicationDetails() {
    this.setSprinner.set(true);
    const responseData = await this.recruitmentManagerService
      .getApplicationDetails(this.viewId)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);

    if (responseData.status === HttpStatusCode.Ok) {

      this.Applicationdetails = responseData.data.responsedata;
      this.editOnboardApplicationFg.controls['designation'].setValue(this.Applicationdetails.designation);
      this.editOnboardApplicationFg.controls['branch'].setValue(this.Applicationdetails.branch);
      this.editOnboardApplicationFg.controls['financialServicesDirector'].setValue(this.Applicationdetails.finacialServiceDir);
      this.editOnboardApplicationFg.controls['financialServicesManager'].setValue(this.Applicationdetails.finacialServiceMan);
      this.editOnboardApplicationFg.controls['name'].setValue(this.Applicationdetails.fullNameNric);
      this.editOnboardApplicationFg.controls['companyName'].setValue(this.Applicationdetails.companyName);
      this.editOnboardApplicationFg.controls['preferredName'].setValue(this.Applicationdetails.preferredName);
      this.editOnboardApplicationFg.controls['nameofReferral'].setValue(this.Applicationdetails.nameOfReferal);
      this.editOnboardApplicationFg.controls['personalEmailAddress'].setValue(this.Applicationdetails.personalEmailAddress);
      this.editOnboardApplicationFg.controls['remarksbyRecruitmentManager'].setValue(this.Applicationdetails.remarkContent);

    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  async saveOnboardApplication() {
    this.setSprinner.set(true);
    let updateApplication = new OnboardingApplication();

    updateApplication.setId(this.Applicationdetails.id);
    updateApplication.setFullNameNric(this.changeFullName(this.editOnboardApplicationFg.get('name')?.value));
    updateApplication.setCompanyName(this.changeFullName(this.editOnboardApplicationFg.get('companyName')?.value));
    updateApplication.setPreferredName(this.changeFullName(this.editOnboardApplicationFg.get('preferredName')?.value));
    updateApplication.setBranch(this.editOnboardApplicationFg.get('branch')?.value);
    updateApplication.setFinacialServiceDir(this.editOnboardApplicationFg.get('financialServicesDirector')?.value);
    updateApplication.setFinacialServiceMan(this.editOnboardApplicationFg.get('financialServicesManager')?.value);
    updateApplication.setDesignation(this.editOnboardApplicationFg.get('designation')?.value);
    updateApplication.setPersonalEmailAddres(this.editOnboardApplicationFg.get('personalEmailAddress')?.value);
    updateApplication.setNameOfReferal(this.editOnboardApplicationFg.get('nameofReferral')?.value);
    updateApplication.setremarkId(this.Applicationdetails.remarkId);
    updateApplication.setRemarkContent(this.editOnboardApplicationFg.get('remarksbyRecruitmentManager')?.value);
    updateApplication.setUpdatedByRole(this.sessionStorageService.getCurrentUserRole());
    updateApplication.setPiasRepCode(this.sessionStorageService.getCurrentRepCode());

    const responseData = await this.recruitmentManagerService
      .editSaveApplication(updateApplication)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {
      this.snackBarService.openSuccessSnackBar('Application saved successfully!');
      // this.router.navigate(['/agent-onboarding']);
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  async submitOnboardApplication() {
    this.setSprinner.set(true);
    let updateApplication = new OnboardingApplication();

    updateApplication.setId(this.Applicationdetails.id);
    updateApplication.setFullNameNric(this.changeFullName(this.editOnboardApplicationFg.get('name')?.value));
    updateApplication.setCompanyName(this.changeFullName(this.editOnboardApplicationFg.get('companyName')?.value));
    updateApplication.setPreferredName(this.changeFullName(this.editOnboardApplicationFg.get('preferredName')?.value));
    updateApplication.setBranch(this.editOnboardApplicationFg.get('branch')?.value);
    updateApplication.setFinacialServiceDir(this.editOnboardApplicationFg.get('financialServicesDirector')?.value);
    updateApplication.setFinacialServiceMan(this.editOnboardApplicationFg.get('financialServicesManager')?.value);
    updateApplication.setDesignation(this.editOnboardApplicationFg.get('designation')?.value);
    updateApplication.setPersonalEmailAddres(this.editOnboardApplicationFg.get('personalEmailAddress')?.value);
    updateApplication.setNameOfReferal(this.editOnboardApplicationFg.get('nameofReferral')?.value);
    updateApplication.setremarkId(this.Applicationdetails.remarkId);
    updateApplication.setRemarkContent(this.editOnboardApplicationFg.get('remarksbyRecruitmentManager')?.value);
    updateApplication.setUpdatedByRole(this.sessionStorageService.getCurrentUserRole());
    updateApplication.setPiasRepCode(this.sessionStorageService.getCurrentRepCode());

    const responseData = await this.recruitmentManagerService
      .editSubmitApplication(updateApplication)
      .then((response) => {
        return JSON.parse(response);
      })
      .catch((error) => {
        // console.error(error);
        return error;
      });
    this.setSprinner.set(false);
    if (responseData.status === HttpStatusCode.Ok) {
      this.snackBarService.openSuccessSnackBar("Application submitted successfully!");
      this.router.navigate(['/agent-onboarding']);
    } else {
      if (responseData.code === HttpStatusCode.BadRequest) {
        // Display notification on API failure.
        this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
      }
      if (responseData.code === HttpStatusCode.Unauthorized) {
        this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
        this.router.navigate(['/login']);
      }
    }
  }

  changeFullName(fullName: string): string {
    return fullName
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  async openConfirmDialog() {
    const dialogRef = this.matDialog.open(ConfirmPopupComponent, {
      width: '500px',
      disableClose: true,
      data: {
        message: ' Are you sure you want to submit this Application?',
        hidecancel: false,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.data === true) {
        // console.log('User confirmed delete');
        // call save API here
        this.submitOnboardApplication();
      } else {
        // console.log('User cancelled');
      }
    });
  }

}
